import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { IconButton, Tooltip, Typography } from '@mui/material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useUnreadNotificationCount } from 'features/notifications/lib/NotificationFeedContext';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useAutoOpenOnboarding } from 'features/onboarding/lib/useAutoOpenOnboarding';
import { useControlCenterNotifications } from 'features/onboarding/lib/useControlCenterNotifications';
import type { ControlCenterTab } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useChecklistPane } from 'features/onboarding/ui/ChecklistPane';
import { ControlCenterActions, ControlCenterTabs } from 'features/onboarding/ui/ControlCenterHeader';
import { useNotificationsPane } from 'features/onboarding/ui/NotificationsPane';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';
import { useAuth } from 'shared/lib/auth/useAuth';
import { PAGE_BANNERS_HEIGHT_VAR } from 'shared/lib/layout/usePageBannersHeight';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { SwipeSheet } from 'shared/ui/SwipeSheet';

import ChevronDoubleRightIcon from './icons/chevron-double-right.svg?react';
import { EDGE_SAFE_TOOLTIP } from './tooltipSlotProps';

export const CONTROL_CENTER_TITLE = 'Control center';
const HIDE_LABEL = 'Hide control center';
// The design's easing, shared with the page-width transition in app/styles/style.css so the panel
// and the content it displaces move on one curve.
const SLIDE = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

// Mounts the control center — the assistant chat, with the setup checklist and the notification feed
// as tabs above it — as a global panel driven by useOnboardingDialogStore. Opening it automatically
// the first time is useAutoOpenOnboarding's job; after that the user opens it via the launcher, or
// the nav drawer on a phone.
//
// This is mounted in App.tsx outside AuthGuard, so it relies on useAssistantEnabled to check app
// access — otherwise it renders over the "Account pending" screen and calls endpoints that answer
// 403 for those users.
//
// Gated out here rather than inside the body, as ChatIntegration does for Inkeep: the checklist
// hooks below fetch the onboarding state without an `enabled` of their own, so a body that bails on
// its last line would still have made the request.
export function OnboardingDialog() {
  return useAssistantEnabled() ? <OnboardingDialogContent /> : null;
}

/**
 * Leaves the sheet behind when the user follows a link out of it.
 *
 * Only on a phone: there the sheet covers the page, so navigating without this lands the user on
 * the target page with the chat still over it. A desktop panel leaves the page visible beside it,
 * and closing it there would throw away a conversation the user can still see.
 */
function useCloseOnNavigate(enabled: boolean) {
  const { pathname } = useLocation();
  const close = useOnboardingDialogStore((state) => state.close);
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (previousPathRef.current === pathname) {
      return;
    }
    previousPathRef.current = pathname;
    if (enabled) {
      close();
    }
  }, [pathname, enabled, close]);
}

/** Restores where this user left the panel, as soon as it is known who they are. */
function useHydrateLayout() {
  const userId = useAuth().user?.id;
  const hydrate = useOnboardingDialogStore((state) => state.hydrate);

  useEffect(() => {
    if (userId) {
      hydrate(userId);
    }
  }, [userId, hydrate]);
}

/**
 * The tab the panel shows. A stored Notifications tab falls back to the assistant for an account that
 * has since lost the feed, rather than showing a tab bar with nothing selected.
 */
function useActiveTab(hasNotifications: boolean): ControlCenterTab {
  const tab = useOnboardingDialogStore((state) => state.tab);
  return hasNotifications ? tab : 'assistant';
}

function OnboardingDialogContent() {
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const close = useOnboardingDialogStore((state) => state.close);
  const hasNotifications = useControlCenterNotifications();
  const tab = useActiveTab(hasNotifications);
  const prefersReducedMotion = useReducedMotion();
  const { isMobile } = useScreenSize();
  const { data: onboardingState } = useOnboardingState();

  useHydrateLayout();
  useCloseOnNavigate(isMobile);
  useAutoOpenOnboarding({ enabled: true, onboardingState });

  // The header's actions don't fit in the sheet's own title row, so a phone carries them on the tab
  // row, and the sheet's close control stands in for the hide button.
  if (isMobile) {
    return (
      <SwipeSheet
        flush
        className="h-[90dvh]"
        closeLabel={HIDE_LABEL}
        open={isOpen}
        title={CONTROL_CENTER_TITLE}
        onClose={close}
      >
        <ControlCenterBody
          tab={tab}
          hasNotifications={hasNotifications}
          tabActions={hasNotifications && <ControlCenterActions tab={tab} />}
        />
      </SwipeSheet>
    );
  }

  // AnimatePresence keeps the panel mounted through its exit, so closing slides it off the right edge
  // instead of blinking out.
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-label={CONTROL_CENTER_TITLE}
          className="assistant-panel-elevation fixed right-0 bottom-0 z-[1300] flex w-assistant-panel flex-col border-grey30 border-l bg-white"
          // Starts below the fixed page banners, so nav, page and panel share one top edge.
          style={{ top: `var(${PAGE_BANNERS_HEIGHT_VAR}, 0px)` }}
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={prefersReducedMotion ? { duration: 0 } : SLIDE}
        >
          <div className="flex h-full min-h-0 flex-col">
            {/* h-20 is the kit page header's HEADER_HEIGHT, so the two read as one bar across the screen. */}
            <div className="relative flex h-20 flex-none items-center gap-3 border-grey30 border-b px-6">
              <Tooltip title={HIDE_LABEL} placement="bottom" slotProps={EDGE_SAFE_TOOLTIP}>
                <IconButton
                  aria-label={HIDE_LABEL}
                  onClick={close}
                  // Centred on the panel's left edge, so half of it sits over the page. border-solid because
                  // ButtonBase's `border: 0` also resets the style, which a width class leaves alone.
                  className="-left-4 -translate-y-1/2 absolute top-1/2 h-8 w-8 border-[1px] border-grey30 border-solid bg-white p-0 text-grey60 hover:border-grey40 hover:bg-white"
                >
                  <ChevronDoubleRightIcon width={20} height={20} />
                </IconButton>
              </Tooltip>
              <Typography className="min-w-0 flex-1 font-bold text-[1.375rem] text-grey90 leading-tight">
                {CONTROL_CENTER_TITLE}
              </Typography>
              {hasNotifications && <ControlCenterActions tab={tab} />}
            </div>
            <ControlCenterBody tab={tab} hasNotifications={hasNotifications} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type ControlCenterBodyProps = {
  tab: ControlCenterTab;
  hasNotifications: boolean;
  tabActions?: ReactNode;
};

/**
 * Everything under the header. Mounted only while the panel is open, so each open re-reads the
 * checklist: finishing setup leaves it on screen for this open, and the next one is just a chat.
 */
function ControlCenterBody({ tab, hasNotifications, tabActions }: ControlCenterBodyProps) {
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const unreadCount = useUnreadNotificationCount();
  const checklistPane = useChecklistPane();
  const notificationsPane = useNotificationsPane();
  const pane = tab === 'notifications' ? notificationsPane : checklistPane;

  return (
    <>
      {/* One tab is no choice, so an account without the feed sees the assistant on its own. */}
      {hasNotifications && (
        <ControlCenterTabs
          tab={tab}
          isChecklistOpen={checklistPane.isListOpen}
          unreadCount={unreadCount}
          actions={tabActions}
        />
      )}
      {pane.strip}
      {/* One conversation under both tabs, so switching keeps its scroll and any answer in flight. */}
      <OnboardingConversation key={sessionKey} list={pane.list} isListOpen={pane.isListOpen} onAsk={pane.onAsk} />
    </>
  );
}
